import { useRef } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { ButtonBack, ButtonNext, Slide, Slider } from 'pure-react-carousel';
import { meta } from '../../../../meta';
import { getImagesByQuality, SIZE_TYPE } from '../../../helpers';
import { useLazyImageProps } from '../../lazy-loading';
import { ArrowOne } from '../slider/arrows/ArrowOne';

function ThumbnailsSecondSlideImage({ id, src, alt, ...forwardingProps }) {
  const imageRef = useRef(null);
  const { lazyLoaded, lazyImageVisibility } = useLazyImageProps(id, imageRef);
  const imageUrl = lazyLoaded ? src : null;

  return (
    <Image
      ref={imageRef}
      src={imageUrl}
      $visibility={lazyImageVisibility}
      alt={alt}
      {...forwardingProps}
    />
  );
}

export const ThumbnailsSecondSlider = ({
  totalSlides,
  images,
  setCurrentItemId,
  setCurrentVisibleItemId,
  checkCopyProtection,
  currentItemId
}) => {
  const secondRef = useRef(null);
  const currentImages = getImagesByQuality(images, SIZE_TYPE.SMALL);

  if (!totalSlides) {
    return null;
  }

  return (
    <Container>
      <Slider
        ref={secondRef}
        onTransitionEnd={() => {
          secondRef.current.instance.setState({
            deltaX: 0,
            deltaY: 0
          });
          secondRef.current.instance.setState({
            cancelNextClick: false
          });
          setCurrentVisibleItemId(secondRef.current.state.currentSlide);
        }}
        onContextMenu={(e) => {
          checkCopyProtection(e);
        }}
      >
        {currentImages.map((item, index) => (
          <StyledSlide
            key={item.id}
            index={index}
            $isActive={currentItemId === index}
            onMouseUp={() => {
              if (!secondRef.current.instance.state.cancelNextClick) {
                setCurrentItemId(index);
              }
            }}
          >
            <ThumbnailsSecondSlideImage
              id={item.id}
              src={item.image?.url}
              alt={item.alt || item.title}
            />
          </StyledSlide>
        ))}
      </Slider>

      <StyledButtonBack>
        <ArrowOne rotate />
      </StyledButtonBack>

      <StyledButtonNext>
        <ArrowOne />
      </StyledButtonNext>
    </Container>
  );
};

const Container = styled.div.attrs({
  className: `${meta.stylePrefix}-thumbnails-second`
})`
  width: 100%;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;

  & > .carousel {
    margin: auto 0;
  }

  & .carousel__inner-slide {
    padding: 5px;
  }
`;

const Image = styled.img.attrs({
  className: classNames(
    `${meta.stylePrefix}-thumbnails-thumbnailImage`,
    `${meta.stylePrefix}-thumbnails-image`
  )
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  visibility: ${({ $visibility }) => $visibility || 'visible'};
`;

const StyledSlide = styled(Slide).attrs(({ $isActive }) => ({
  className: classNames(
    `${meta.stylePrefix}-thumbnails-thumbnailImageContainer`,
    {
      [`${meta.stylePrefix}-thumbnails-thumbnailActive`]: $isActive
    }
  )
}))`
  position: relative;
  opacity: 0.7;
  transition: 0.3s ease opacity;
  cursor: pointer;
  outline: none;

  &::after {
    transition:
      0.3s ease opacity,
      0.3s ease border;
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid transparent;
    pointer-events: none;
  }

  &:hover::after {
    border: 2px solid rgba(17, 17, 17, 0.7);
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      opacity: 1;

      &::after {
        border: 2px solid #111;
      }

      &:hover::after {
        border: 2px solid #111;
      }
    `}
`;

const NavButtonStyles = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  background-color: transparent;
  border-radius: 20px;
  border: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 101%;
  transition: 0.3s ease opacity;
  opacity: 1;
  z-index: 10;
  cursor: pointer;

  &:disabled {
    opacity: 0;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    width: 60px;
    pointer-events: none;
    height: 100%;
  }

  & svg {
    width: 20px;
    height: 20px;
    fill: #111;
  }
`;

const StyledButtonBack = styled(ButtonBack).attrs({
  className: classNames(
    `${meta.stylePrefix}-thumbnails-secondButton`,
    `${meta.stylePrefix}-thumbnails-secondButtonBack`
  )
})`
  ${NavButtonStyles};
  left: 0;

  &::after {
    left: 0;
  }
`;

const StyledButtonNext = styled(ButtonNext).attrs({
  className: classNames(
    `${meta.stylePrefix}-thumbnails-secondButton`,
    `${meta.stylePrefix}-thumbnails-secondButtonNext`
  )
})`
  ${NavButtonStyles};
  right: 0;

  &::after {
    right: 0;
  }
`;
